import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes } from "styled-components";
import { CloseIcon } from "../atoms/icons/CloseIcon";

type ModalTemplateProps = {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
};

export const ModalTemplate: React.FC<ModalTemplateProps> = ({
  isOpen,
  title,
  onClose,
  children,
  maxWidth = "900px",
}) => {
  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store original body and document element styles
      const body = document.body;
      const documentElement = document.documentElement;
      
      const originalBodyOverflow = body.style.overflow;
      const originalBodyPosition = body.style.position;
      const originalBodyTop = body.style.top;
      const originalBodyWidth = body.style.width;
      const originalDocumentOverflow = documentElement.style.overflow;
      
      // Get current scroll position
      const scrollY = window.scrollY;
      const scrollBarWidth = window.innerWidth - documentElement.clientWidth;
      
      // Detect Safari version for compatibility
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const safariVersion = isSafari ? parseInt(navigator.userAgent.match(/version\/(\d+)/i)?.[1] || '0') : 0;
      
      // Check if 100dvh is supported
      const supports100dvh = CSS.supports('height', '100dvh');
      
      // Apply scroll lock with Safari 12 compatibility
      body.style.overflow = 'hidden';
      
      // Only use the old approach for Safari 12 specifically
      // Safari 13+ and all other modern browsers should use the modern approach
      if (isSafari && safariVersion === 12) {
        // For Safari 12 specifically, use a simpler approach that doesn't break modal positioning
        body.style.position = 'relative';
        body.style.top = '0';
        body.style.width = '100%';
        body.style.paddingRight = `${scrollBarWidth}px`;
      } else {
        // Modern approach for Safari 13+, Chrome, Firefox, Edge, and all other browsers
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';
        body.style.paddingRight = `${scrollBarWidth}px`;
      }
      
      documentElement.style.overflow = 'hidden';
      
      // Prevent touch scrolling on iOS - but only outside modal content
      const preventTouchMove = (e: TouchEvent) => {
        // Allow scrolling within modal content
        const target = e.target as Element;
        const modalContent = document.querySelector('[data-modal-content]');
        if (modalContent && modalContent.contains(target)) {
          return; // Allow scrolling within modal
        }
        e.preventDefault();
      };
      
      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      
      // Cleanup function to restore original state
      return () => {
        body.style.overflow = originalBodyOverflow;
        body.style.position = originalBodyPosition;
        body.style.top = originalBodyTop;
        body.style.width = originalBodyWidth;
        body.style.paddingRight = '';
        documentElement.style.overflow = originalDocumentOverflow;
        
        document.removeEventListener('touchmove', preventTouchMove);
        
        // Restore scroll position for all browsers except Safari 12
        if (!(isSafari && safariVersion === 12)) {
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Handle background click to close modal
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <ModalBackground onClick={handleBackgroundClick}>
      <ModalContainer style={{ maxWidth }} data-modal-content>
        <Header>
          <Title>{title}</Title>
          <CloseBtn onClick={onClose}>
            <CloseIcon />
          </CloseBtn>
        </Header>
        <Content>{children}</Content>
      </ModalContainer>
    </ModalBackground>,
    document.body
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const ModalBackground = styled.div`
  position: fixed; top: 0; left: 0; width: 100vw; 
  height: 100vh; /* Fallback for older browsers */
  height: 100dvh; /* Modern browsers - will override 100vh if supported */
  background: rgba(0,0,0,0.3); z-index: 10000;
  
  /* Only apply -webkit-fill-available for browsers that don't support 100dvh */
  @supports not (height: 100dvh) {
    height: -webkit-fill-available;
  }
`;

const ModalContainer = styled.div`
  position: absolute; top: 50%; left: 50%;
  width: 90vw; max-width: 900px;
  background: #fff; border-radius: 8px;
  transform: translate(-50%, -50%);
  animation: ${fadeIn} 0.3s;
  border-radius: 8px 8px;
  max-height: 90vh;
  overflow-y: auto;
  
  /* Safari 12 specific positioning fixes - only apply to browsers without 100dvh support */
  @supports not (height: 100dvh) {
    /* Ensure modal stays centered in Safari 12 and other older browsers */
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    /* Add hardware acceleration for smoother rendering */
    -webkit-backface-visibility: hidden;
    -webkit-perspective: 1000;
  }
`;

const Header = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 24px;
  background-color: #f1f3f5;
  border-radius: 8px 8px 0 0;
  padding: 24px;
`;

const Title = styled.h2`
  font-size: 20px; font-weight: 600; margin: 0;
`;

const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer;
`;

const Content = styled.div`
  padding: 0px 24px calc(32px + env(safe-area-inset-bottom)) 24px;
  max-height: 80vh; /* Fallback for older browsers */
  max-height: 80dvh; /* Modern browsers - will override 80vh if supported */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
`;
